type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private readonly logLevel: LogLevel;

  constructor() {
    const isBrowser = typeof window !== 'undefined';
    const isDev = process.env.NODE_ENV === 'development';
    // In non-dev browser environments, suppress debug/info by default
    if (isBrowser && !isDev) {
      this.logLevel = 'warn';
      return;
    }
    // Otherwise honor env vars with a sensible default
    const envLevel =
      process.env.NEXT_PUBLIC_LOG_LEVEL || process.env.LOG_LEVEL || 'info';
    this.logLevel = this.parseLogLevel(envLevel);
  }

  private parseLogLevel(level: string): LogLevel {
    const validLevels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const lowerLevel = level.toLowerCase() as LogLevel;
    return validLevels.includes(lowerLevel) ? lowerLevel : 'info';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentIndex = levels.indexOf(this.logLevel);
    const messageIndex = levels.indexOf(level);
    return messageIndex >= currentIndex;
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext,
  ): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    let out: (msg?: any, ...args: any[]) => void = console.log;
    if (level === 'error' && typeof console.error === 'function') {
      out = console.error.bind(console);
    } else if (level === 'warn' && typeof console.warn === 'function') {
      out = console.warn.bind(console);
    } else if (typeof console.log === 'function') {
      out = console.log.bind(console);
    }
    if (context && Object.keys(context).length > 0) {
      out(`${prefix} ${message}`, context);
    } else {
      out(`${prefix} ${message}`);
    }
  }

  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;
    if (typeof console.debug === 'function') {
      if (context) console.debug(message, context);
      else console.debug(message);
    } else {
      if (context) console.log(message, context);
      else console.log(message);
    }
  }

  info(message: string, context?: LogContext): void {
    this.formatMessage('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.formatMessage('warn', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.formatMessage('error', message, context);
  }
}

// Export singleton instance
export const logger = new Logger();

// Alias for backwards compatibility with code that imported clientLogger
export const clientLogger = logger;
